import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  Star, 
  ArrowRight, 
  X,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  CreditCard,
  MessageCircle,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const EnhancedStudentDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('book');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingConfirm, setShowBookingConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [bookings, setBookings] = useState([]);

  // Load data with error handling
  useEffect(() => {
    loadTeachers();
    loadBookings();
  }, []);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      // Simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const mockTeachers = [
        {
          id: 1,
          name: "Sarah Mitchell",
          avatar: "👩‍🏫",
          rating: 4.9,
          reviews: 156,
          specialties: ["Business English", "Presentations", "IELTS"],
          experience: "8 years",
          price: 85,
          tier: "Premium",
          bio: "Experienced business English teacher with expertise in presentation skills and professional communication.",
          nextAvailable: "Today 2:00 PM"
        },
        {
          id: 2,
          name: "James Wilson",
          avatar: "👨‍🏫",
          rating: 4.7,
          reviews: 89,
          specialties: ["Conversation", "Grammar", "Business"],
          experience: "5 years",
          price: 65,
          tier: "Standard",
          bio: "Passionate about helping students improve their conversational English and grammar skills.",
          nextAvailable: "Tomorrow 10:00 AM"
        },
        {
          id: 3,
          name: "Maria García",
          avatar: "👩‍💼",
          rating: 4.8,
          reviews: 234,
          specialties: ["Spanish Speakers", "Pronunciation", "Fluency"],
          experience: "12 years",
          price: 85,
          tier: "Premium",
          bio: "Native Spanish speaker who understands the challenges of learning English. Specializes in pronunciation.",
          nextAvailable: "Today 4:00 PM"
        }
      ];
      
      setTeachers(mockTeachers);
      setError('');
    } catch (err) {
      console.error('Failed to load teachers:', err);
      setError('Could not load teachers, please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const mockBookings = [
        {
          id: 1,
          teacher: teachers[0] || { name: "Sarah Mitchell", avatar: "👩‍🏫" },
          date: "2024-12-18",
          time: "14:00",
          duration: 55,
          status: "confirmed",
          type: "Business English",
          meetingLink: "https://meet.google.com/abc-def-ghi"
        },
        {
          id: 2,
          teacher: teachers[2] || { name: "Maria García", avatar: "👩‍💼" },
          date: "2024-12-20",
          time: "16:00",
          duration: 85,
          status: "confirmed",
          type: "Pronunciation Practice",
          meetingLink: "https://meet.google.com/xyz-abc-def"
        }
      ];
      
      setBookings(mockBookings);
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError('Could not load bookings, please try again.');
    }
  };

  const ErrorFallback = ({ message, onRetry }) => (
    <div className="text-center py-12">
      <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
      <p className="text-gray-600 mb-4">{message}</p>
      <button 
        onClick={onRetry}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
      >
        <RefreshCw className="w-4 h-4" />
        <span>Try Again</span>
      </button>
    </div>
  );

  const LoadingSpinner = () => (
    <div className="text-center py-12">
      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  );

  const BookTeacherTab = () => {
    if (loading) return <LoadingSpinner />;
    if (error && teachers.length === 0) return <ErrorFallback message={error} onRetry={loadTeachers} />;

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Book a Class</h1>
          <p className="text-gray-600">Choose a teacher and schedule your English lesson</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search teachers by name or specialty..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="w-5 h-5" />
            <span>Filter</span>
          </button>
        </div>

        {/* Teachers Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teachers.map((teacher) => (
            <motion.div
              key={teacher.id}
              whileHover={{ y: -5 }}
              className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              onClick={() => setSelectedTeacher(teacher)}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-2xl">
                  {teacher.avatar}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{teacher.name}</h3>
                  <p className="text-sm text-blue-600">{teacher.tier} Teacher</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-3">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-sm font-medium">{teacher.rating}</span>
                  <span className="text-sm text-gray-500">({teacher.reviews})</span>
                </div>
                <span className="text-sm text-gray-500">{teacher.experience}</span>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{teacher.bio}</p>

              <div className="flex flex-wrap gap-1 mb-4">
                {teacher.specialties.slice(0, 2).map((specialty, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                    {specialty}
                  </span>
                ))}
                {teacher.specialties.length > 2 && (
                  <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">
                    +{teacher.specialties.length - 2}
                  </span>
                )}
              </div>

              <div className="flex justify-between items-center">
                <div className="text-lg font-bold text-gray-900">€{teacher.price}/hour</div>
                <div className="text-sm text-green-600">{teacher.nextAvailable}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Teacher Selection Modal */}
        {selectedTeacher && (
          <EnhancedTeacherBookingModal 
            teacher={selectedTeacher}
            onClose={() => setSelectedTeacher(null)}
            onBookingConfirm={() => {
              setShowBookingConfirm(true);
              setSelectedTeacher(null);
            }}
          />
        )}

        {/* Booking Confirmation */}
        {showBookingConfirm && (
          <BookingConfirmationModal onClose={() => setShowBookingConfirm(false)} />
        )}
      </div>
    );
  };

  const MyBookingsTab = () => {
    if (loading) return <LoadingSpinner />;
    if (error && bookings.length === 0) return <ErrorFallback message={error} onRetry={loadBookings} />;

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">My Bookings</h1>
          <p className="text-gray-600">Manage your scheduled and completed classes</p>
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No bookings yet</h3>
            <p className="text-gray-600 mb-4">Book your first class to get started!</p>
            <button 
              onClick={() => setActiveTab('book')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Book a Class
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-2xl">
                      {booking.teacher.avatar}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{booking.teacher.name}</h3>
                      <p className="text-sm text-gray-600">{booking.type}</p>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                        <span>{new Date(booking.date).toLocaleDateString()}</span>
                        <span>{booking.time}</span>
                        <span>{booking.duration} min</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800'
                        : booking.status === 'completed'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                    
                    {booking.status === 'confirmed' && (
                      <div className="flex space-x-2">
                        <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm">
                          Join Class
                        </button>
                        <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                          Reschedule
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {booking.notes && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Teacher Notes:</strong> {booking.notes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">L</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900">Lexora Student</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{user.name}</span>
              </div>
              <button 
                onClick={onLogout}
                className="text-gray-500 hover:text-gray-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'book', label: 'Book a Class', icon: Calendar },
              { id: 'bookings', label: 'My Bookings', icon: Clock },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'book' && <BookTeacherTab />}
        {activeTab === 'bookings' && <MyBookingsTab />}
      </main>
    </div>
  );
};

// Enhanced Teacher Booking Modal with validation
const EnhancedTeacherBookingModal = ({ teacher, onClose, onBookingConfirm }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('');
  const [duration, setDuration] = useState('55');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const availableSlots = [
    { time: "09:00", available: true },
    { time: "10:00", available: false },
    { time: "11:00", available: true },
    { time: "14:00", available: true },
    { time: "15:00", available: true },
    { time: "16:00", available: false },
    { time: "17:00", available: true },
  ];

  const validateBooking = () => {
    const newErrors = {};

    // Date validation
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDateObj < today) {
      newErrors.date = 'Please select a future date';
    }

    // Time validation
    if (!selectedTime) {
      newErrors.time = 'Please select a time slot';
    }

    // Duration validation
    if (!duration || !['55', '85'].includes(duration)) {
      newErrors.duration = 'Please select a valid duration';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBooking = async () => {
    if (!validateBooking()) return;

    try {
      setLoading(true);
      
      // Simulate booking API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Here would be the actual Supabase booking creation
      console.log('Booking created:', {
        teacherId: teacher.id,
        date: selectedDate,
        time: selectedTime,
        duration: parseInt(duration),
        price: duration === '55' ? teacher.price : Math.round(teacher.price * 1.5)
      });
      
      onBookingConfirm();
    } catch (error) {
      console.error('Booking failed:', error);
      setErrors({ general: 'Failed to create booking. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto"
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Book with {teacher.name}</h2>
            <p className="text-gray-600">{teacher.tier} Teacher • €{teacher.price}/hour</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {errors.general && (
          <div className="p-6 bg-red-50 border-b border-red-200">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{errors.general}</span>
            </div>
          </div>
        )}

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date *
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setErrors(prev => ({ ...prev, date: undefined }));
              }}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.date ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.date && <p className="text-red-600 text-sm mt-1">{errors.date}</p>}
          </div>

          {/* Duration Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class Duration *
            </label>
            <select
              value={duration}
              onChange={(e) => {
                setDuration(e.target.value);
                setErrors(prev => ({ ...prev, duration: undefined }));
              }}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.duration ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="55">55 minutes (€{teacher.price})</option>
              <option value="85">85 minutes (€{Math.round(teacher.price * 1.5)})</option>
            </select>
            {errors.duration && <p className="text-red-600 text-sm mt-1">{errors.duration}</p>}
          </div>
        </div>

        {/* Time Slots */}
        <div className="p-6 border-t border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Times *</h3>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {availableSlots.map((slot) => (
              <button
                key={slot.time}
                disabled={!slot.available}
                onClick={() => {
                  setSelectedTime(slot.time);
                  setErrors(prev => ({ ...prev, time: undefined }));
                }}
                className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                  selectedTime === slot.time
                    ? 'bg-blue-600 text-white border-blue-600'
                    : slot.available
                    ? 'border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                    : 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>
          {errors.time && <p className="text-red-600 text-sm mt-2">{errors.time}</p>}
        </div>

        {/* Booking Summary */}
        {selectedTime && (
          <div className="p-6 border-t border-gray-100 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Teacher:</span>
                <span className="font-medium">{teacher.name}</span>
              </div>
              <div className="flex justify-between">
                <span>Date & Time:</span>
                <span className="font-medium">{selectedDate} at {selectedTime}</span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span className="font-medium">{duration} minutes</span>
              </div>
              <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                <span>Total:</span>
                <span>€{duration === '55' ? teacher.price : Math.round(teacher.price * 1.5)}</span>
              </div>
            </div>
            
            <button
              onClick={handleBooking}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors mt-4 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  <span>Confirm Booking</span>
                </>
              )}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Booking Confirmation Modal
const BookingConfirmationModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-3xl max-w-md w-full p-8 text-center"
    >
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
      <p className="text-gray-600 mb-6">
        Your class has been booked successfully. You'll receive a confirmation email with the meeting link shortly.
      </p>
      <button 
        onClick={onClose}
        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Got it
      </button>
    </motion.div>
  </div>
);

export default EnhancedStudentDashboard;